import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import { AuditTrailItemType } from '@/common/enums/audit-trail-item-type.enum';
import AuditTrailItemChange from '@/common/models/audit/AuditTrailItemChange';
import Exception from '@/common/models/Exception';
import IABCategories from '@/common/models/iab-category/IABCategories';
import i18n from '@/vueI18n';

import AuditItemType from './AuditItemType.enum';

export default class AuditTrailItem {
  mainEntityName: string;

  entityName: string;

  type: AuditTrailItemType;

  index: number;

  datetime: DateTime;

  user: string;

  profilePictureUrl: string;

  entity;

  changes: AuditTrailItemChange[] | null;

  isVisible: boolean;

  constructor(options: {
    mainEntityName: string;
    entityName: string;
    type?: AuditTrailItemType;
    index: number;
    time: DateTime;
    user: string;
    profilePictureUrl: string;
    entity;
  }) {
    this.mainEntityName = options.mainEntityName;
    this.entityName = options.entityName;
    this.type = options.type;
    this.index = options.index;
    this.datetime = options.time;
    this.user = options.user;
    this.profilePictureUrl = options.profilePictureUrl;
    this.entity = options.entity;
    this.changes = null;
    this.isVisible = true;
  }

  get isMainEntity(): boolean {
    return this.entityName.localeCompare(this.mainEntityName) === 0;
  }

  get hoursAsDecimal(): number {
    return this.datetime.diff(this.datetime.startOf('day'), 'hours').hours;
  }

  get hoursAngleInDegrees(): number {
    return ((this.hoursAsDecimal % 12) / 12) * 360;
  }

  get minutesAsDecimal(): number {
    return this.datetime.diff(this.datetime.startOf('hour'), 'minutes').minutes;
  }

  get minutesAngleInDegrees(): number {
    return (this.minutesAsDecimal / 60) * 360;
  }

  get dateFormatted(): string {
    return this.datetime.toLocaleString(DateTime.DATETIME_FULL);
  }

  get timeFormatted(): string {
    return this.datetime.toLocaleString(DateTime.TIME_SIMPLE);
  }

  get title(): string {
    return `${this.action} ${this.entityNameTranslated} ${this.entityDescription}`;
  }

  get action(): string {
    switch (this.type) {
      case AuditTrailItemType.CREATE: {
        return this.entityName.localeCompare(this.mainEntityName) !== 0
          ? i18n.global.t('messages.auditTrailAddedEntity')
          : i18n.global.t('messages.auditTrailCreatedEntity');
      }
      case AuditTrailItemType.UPDATE: {
        return i18n.global.t('messages.auditTrailModifiedEntity');
      }
      case AuditTrailItemType.DELETE: {
        return i18n.global.t('messages.auditTrailRemovedEntity');
      }
      default: {
        throw new Exception('Unknown audit trail operation type.');
      }
    }
  }

  get entityNameTranslated(): string {
    let type = this.entityName;
    switch (this.entityName) {
      // Line Item
      case AuditItemType.LineItem: {
        type = capitalize(i18n.global.t('names.lineItem'));
        break;
      }
      case AuditItemType.LineItemCreative: {
        if (this.mainEntityName === 'Creative') {
          type = capitalize(i18n.global.t('names.lineItem'));
        } else {
          type = capitalize(i18n.global.t('names.creative'));
        }
        break;
      }
      case AuditItemType.LineItemDemographicTarget: {
        if (this.mainEntityName === 'Demographic') {
          type = capitalize(i18n.global.t('names.lineItem'));
        } else {
          type = capitalize(i18n.global.t('names.demographic'));
        }
        break;
      }
      case AuditItemType.LineItemProperty: {
        switch (this.entity.key) {
          case 'beta-audience':
            type = capitalize(i18n.global.t('names.audienceSegment'));
            break;
          default:
            break;
        }
        break;
      }
      case AuditItemType.LineItemTargetingCriterion: {
        type = this.entity.targetingcriterion_name;
        break;
      }
      case AuditItemType.LineItemTargetingCriterionValue: {
        type = 'Targeting';
        break;
      }

      // Creative
      case AuditItemType.Creative: {
        type = capitalize(i18n.global.t('names.creative'));
        break;
      }
      case AuditItemType.CreativeFile: {
        type = capitalize(i18n.global.t('names.creativeFile'));
        break;
      }
      case AuditItemType.CreativeIab: {
        type = capitalize(i18n.global.t('names.iabCategory'));
        break;
      }
      case AuditItemType.UnitLanguage:
      case AuditItemType.CreativeLanguage: {
        type = capitalize(i18n.global.t('names.language'));
        break;
      }
      case AuditItemType.CreativeTrackingUrl: {
        type = capitalize(i18n.global.t('names.creativeTrackingUrl'));
        break;
      }
      case AuditItemType.UnitProperty:
      case AuditItemType.CreativeProperty: {
        type = capitalize(i18n.global.t('names.property'));
        break;
      }

      // Unit
      case AuditItemType.Unit: {
        type = capitalize(i18n.global.t('names.screen'));
        break;
      }
      case AuditItemType.UnitGeofence: {
        type = capitalize(i18n.global.t('names.geofence'));
        break;
      }
      case AuditItemType.UnitFallbackCreative: {
        if (this.mainEntityName === 'Unit') {
          type = capitalize(i18n.global.t('names.fallbackCreative'));
        } else {
          type = capitalize(i18n.global.t('names.unit'));
        }
        break;
      }
      case AuditItemType.UnitIabBlacklist: {
        type = capitalize(i18n.global.t('names.blacklistedIABCategory'));
        break;
      }
      case AuditItemType.UnitAdvertiserBlacklist: {
        type = capitalize(i18n.global.t('names.blacklistedAdvertiser'));
        break;
      }

      // Mobile Line Item
      case AuditItemType.MobileLineItem: {
        type = capitalize(i18n.global.t('names.mobileLineItem'));
        break;
      }
      case AuditItemType.MobileLineItemCreative: {
        if (this.mainEntityName === 'Creative') {
          type = capitalize(i18n.global.t('names.mobileLineItem'));
        } else {
          type = capitalize(i18n.global.t('names.creative'));
        }
        break;
      }
      case AuditItemType.MobileLineItemLineItem: {
        if (this.mainEntityName === 'MobileLineItem') {
          type = capitalize(i18n.global.t('names.lineItem'));
        } else {
          type = capitalize(i18n.global.t('names.mobileLineItem'));
        }
        break;
      }

      // Aproval
      case AuditItemType.CreativeMediaOwnerApproval:
      case AuditItemType.LineItemMediaOwnerApproval: {
        type = capitalize(i18n.global.t('names.mediaOwnerApproval'));
        break;
      }

      // Report Definition
      case AuditItemType.ReportDefinition: {
        type = capitalize(i18n.global.t('names.reportDefinition'));
        break;
      }
      // Package
      case AuditItemType.Package: {
        type = capitalize(i18n.global.t('names.deal'));
        break;
      }
      // Site
      case AuditItemType.Site: {
        type = capitalize(i18n.global.t('names.site'));
        break;
      }
      // Geofence
      case AuditItemType.GeofenceGroupSite: {
        if (this.mainEntityName === 'Site') {
          type = capitalize(i18n.global.t('names.geofenceGroup'));
        } else {
          type = capitalize(i18n.global.t('names.site'));
        }
        break;
      }

      // Inventory Targeting
      case AuditItemType.SiteTargeting: {
        type = capitalize(i18n.global.t('names.siteTargeting'));
        break;
      }
      case AuditItemType.UnitTargeting: {
        type = capitalize(i18n.global.t('names.unitTargeting'));
        break;
      }
      case AuditItemType.UnitExclusion:
        type = capitalize(i18n.global.t('names.unitExclusion'));
        break;
      case AuditItemType.SiteExclusion:
        type = capitalize(i18n.global.t('names.siteExclusion'));
        break;
      case AuditItemType.MediaOwnerTargeting: {
        type = capitalize(i18n.global.t('names.mediaOwner'));
        break;
      }
      case AuditItemType.LocationTargeting: {
        type = capitalize(i18n.global.t('names.location'));
        break;
      }
      case AuditItemType.LocationExclusion: {
        type = capitalize(i18n.global.t('names.locationExclusion'));
        break;
      }
      case AuditItemType.Network:
      case AuditItemType.NetworkTargeting: {
        type = capitalize(i18n.global.t('names.network'));
        break;
      }
      case AuditItemType.NetworkExclusion: {
        type = capitalize(i18n.global.t('names.networkExclusion'));
        break;
      }
      case AuditItemType.MediatypeTargeting: {
        type = capitalize(i18n.global.t('names.venueType'));
        break;
      }
      case AuditItemType.MediatypeExclusion: {
        type = capitalize(i18n.global.t('names.venueTypeExclusion'));
        break;
      }
      case AuditItemType.UnitMarket:
      case AuditItemType.SiteMarket:
      case AuditItemType.MarketTargeting: {
        type = capitalize(i18n.global.t('names.market'));
        break;
      }
      case AuditItemType.MarketExclusion: {
        type = capitalize(i18n.global.t('names.marketExclusion'));
        break;
      }
      case AuditItemType.GeofenceGroupTargeting: {
        type = capitalize(i18n.global.t('names.geofenceGroup'));
        break;
      }
      case AuditItemType.GeofenceGroupExclusion: {
        type = capitalize(i18n.global.t('names.geofenceGroupExclusion'));
        break;
      }
      case AuditItemType.UnitTagTargeting: {
        type = capitalize(i18n.global.t('names.tag'));
        break;
      }
      case AuditItemType.UnitTagExclusion: {
        type = capitalize(i18n.global.t('names.tagExclusion'));
        break;
      }
      case AuditItemType.LanguageTargeting: {
        type = capitalize(i18n.global.t('names.language'));
        break;
      }
      case AuditItemType.LanguageExclusion: {
        type = capitalize(i18n.global.t('names.languageExclusion'));
        break;
      }
      case AuditItemType.UnitPackTargeting: {
        type = capitalize(i18n.global.t('names.unitPack'));
        break;
      }
      case AuditItemType.UnitPackExclusion: {
        type = capitalize(i18n.global.t('names.unitPackExclusion'));
        break;
      }
      case AuditItemType.CreativeFormatTargeting: {
        type = capitalize(i18n.global.t('names.creativeFormat'));
        break;
      }
      case AuditItemType.CreativeFormatExclusion: {
        type = capitalize(i18n.global.t('names.creativeFormatExclusion'));
        break;
      }
      case AuditItemType.CreativeOrientationTargeting: {
        type = capitalize(i18n.global.t('names.creativeOrientation'));
        break;
      }
      case AuditItemType.CreativeOrientationExclusion: {
        type = capitalize(i18n.global.t('names.creativeOrientationExclusion'));
        break;
      }
      case AuditItemType.IabCategoryTargeting: {
        type = capitalize(i18n.global.t('names.iabCategory'));
        break;
      }
      case AuditItemType.IabCategoryExclusion: {
        type = capitalize(i18n.global.t('names.iabCategoryExclusion'));
        break;
      }
      case AuditItemType.CO2eEmissionsScoreTargeting: {
        type = capitalize(i18n.global.t('names.co2EmissionsScore'));
        break;
      }
      default: {
        break;
      }
    }
    return type;
  }

  get entityDescription(): string {
    const iabCategoryOptions = IABCategories.options();
    let name = this.entity.name !== undefined ? this.entity.name : this.entity[`${this.entityName.toLowerCase()}_id`];
    switch (this.entityName) {
      // Line Item
      case AuditItemType.LineItemCreative:
        if (this.mainEntityName === 'LineItem') {
          name = this.entity.creative_name;
        } else {
          name = this.entity.lineitem_name;
        }
        break;
      case AuditItemType.LineItemDemographicTarget:
        if (this.mainEntityName === 'LineItem') {
          name = this.entity.demographic_name;
        } else {
          name = this.entity.lineitem_name;
        }
        break;
      case AuditItemType.LineItemProperty: {
        switch (this.entity.key) {
          case 'beta-audience':
            name = JSON.parse(this.entity.value).selected?.join(', ');
            break;
          default:
            break;
        }
        break;
      }
      case AuditItemType.LineItemTargetingCriterion: {
        name = this.entity.targetingcriterion_name;
        break;
      }
      case AuditItemType.LineItemTargetingCriterionValue: {
        switch (this.entity.code) {
          case 'WEATHER':
            return capitalize(i18n.global.t('names.weatherCondition', { n: 2 }));
          case 'CURRENT_ABOVE':
          case 'CURRENT_BELOW':
          case 'DAILY_MAX_ABOVE':
          case 'DAILY_MAX_BELOW':
          case 'DAILY_MIN_ABOVE':
          case 'DAILY_MIN_BELOW':
            return capitalize(i18n.global.t('names.temperature'));
          case 'POLLEN_CATEGORY':
            return capitalize(i18n.global.t('names.pollen'));
          default:
            return this.entity.code;
        }
      }

      // Mobile LineItem
      case AuditItemType.MobileLineItemLineItem:
        if (this.mainEntityName === 'MobileLineItem') {
          name = this.entity.lineitem_name;
        } else {
          name = this.entity.mobilelineitem_name;
        }
        break;
      case AuditItemType.MobileLineItemCreative:
        if (this.mainEntityName === 'MobileLineItem') {
          name = this.entity.creative_name;
        } else {
          name = this.entity.mobilelineitem_name;
        }
        break;

      // Creative
      case AuditItemType.UnitLanguage:
      case AuditItemType.CreativeLanguage:
        name = this.entity.language_name;
        break;
      case AuditItemType.CreativeTrackingUrl:
        name = this.entity.trackingurl;
        break;
      case AuditItemType.CreativeProperty:
        name = `${this.entity.key}: ${this.entity.value}`;
        break;
      case AuditItemType.LineItemMediaOwnerApproval:
        name = this.entity.owner_name;
        break;
      case AuditItemType.GeofenceGroupSite:
        if (this.mainEntityName === 'Site') {
          name = this.entity.geofencegroup_name;
        } else {
          name = this.entity.site_name;
        }
        break;

      // Unit
      case AuditItemType.Unit:
        if (this.mainEntityName === 'Site') {
          name = this.entity.unit_name;
        }
        break;
      case AuditItemType.UnitFallbackCreative:
        if (this.mainEntityName === 'Unit') {
          name = this.entity.creative_name;
        } else {
          name = this.entity.unit_name;
        }
        break;
      case AuditItemType.CreativeIab:
      case AuditItemType.UnitIabBlacklist:
        try {
          name = iabCategoryOptions.find(
            (o) => o.id === `IAB${this.entity.level1}${this.entity.level2 ? `-${this.entity.level2}` : ''}`,
          ).description;
        } catch (e) {}
        break;
      case AuditItemType.UnitProperty:
        name = `${this.entity.name}: ${this.entity.value}`;
        break;
      case AuditItemType.UnitAdvertiserBlacklist:
        name = this.entity.uniqueadvertiser_name;
        break;

      // Inventory Targeting
      case AuditItemType.UnitTargeting:
      case AuditItemType.UnitExclusion:
      case AuditItemType.SiteTargeting:
      case AuditItemType.SiteExclusion:
        if (this.mainEntityName === 'LineItem' || this.mainEntityName === 'Creative') {
          name = this.entity.unit_name ? this.entity.unit_name : this.entity.site_name;
        } else {
          name = this.entity.lineitem_name;
        }
        break;
      case AuditItemType.MediaOwnerTargeting: {
        name = this.entity.mediaowner_name ? this.entity.mediaowner_name : this.entity.owner_name;
        break;
      }
      case AuditItemType.LocationTargeting:
      case AuditItemType.LocationExclusion:
        name = this.entity.location_name;
        break;
      case AuditItemType.NetworkTargeting:
      case AuditItemType.NetworkExclusion:
        name = this.entity.network_name;
        break;
      case AuditItemType.MediatypeTargeting:
      case AuditItemType.MediatypeExclusion:
        name = this.entity.mediatype_name;
        break;
      case AuditItemType.UnitMarket:
      case AuditItemType.SiteMarket:
      case AuditItemType.MarketTargeting:
      case AuditItemType.MarketExclusion:
        name = this.entity.market_name;
        break;
      case AuditItemType.GeofenceGroupTargeting:
      case AuditItemType.GeofenceGroupExclusion:
        if (this.mainEntityName === 'LineItem' || this.mainEntityName === 'Creative') {
          name = this.entity.geofencegroup_name;
        } else {
          name = this.entity.lineitem_name;
        }
        break;
      case AuditItemType.UnitTagTargeting:
      case AuditItemType.UnitTagExclusion:
        name = this.entity.tag;
        break;
      case AuditItemType.LanguageTargeting:
      case AuditItemType.LanguageExclusion:
        name = this.entity.language_name;
        break;
      case AuditItemType.UnitPackTargeting:
      case AuditItemType.UnitPackExclusion:
        name = this.entity.unitpack_id;
        break;
      case AuditItemType.CreativeFormatTargeting:
      case AuditItemType.CreativeFormatExclusion:
        name = this.entity.creative_format;
        break;
      case AuditItemType.CreativeOrientationTargeting:
      case AuditItemType.CreativeOrientationExclusion:
        name = this.entity.creative_orientation;
        break;
      case AuditItemType.IabCategoryTargeting:
      case AuditItemType.IabCategoryExclusion:
        name = this.entity.iab_category;
        break;
      case AuditItemType.CO2eEmissionsScoreTargeting:
        name = this.entity.threshold;
        break;
      default: {
        break;
      }
    }
    return name;
  }
}
